import { useCallback, useEffect, useRef, useState } from 'react';
import { ptBR } from '@/i18n/pt-BR';
import { useApiClient } from '@/lib/api';
import { ClientesSection } from '@/components/Admin/ClientesSection';
import { EscritoriosSection } from '@/components/Admin/EscritoriosSection';
import { NovoEscritorioForm } from '@/components/Admin/NovoEscritorioForm';
import { mapAdminError } from '@/components/Admin/messages';
import {
  AdminError,
  criarCliente,
  criarEscritorio,
  definirEncerramento,
  definirPlataforma,
  definirVinculo,
  listClientes,
  listEscritorios,
  type ClienteAdmin,
  type EscritorioAdmin,
  type EstadoAdmin,
} from '@/lib/plataforma_admin';
import { Input, Select } from '@/components/ui';

function _mensagem(err: unknown): string {
  return err instanceof AdminError ? mapAdminError(err.reason) : ptBR.admin.erroGenerico;
}

/**
 * Console `/admin/clientes` — clientes e escritórios (E53.F5, ADR-0064 §4). // ux-neutrality:allow doc-comment
 *
 * Toda ação recarrega as duas listas do servidor: o vínculo muda a contagem do // ux-neutrality:allow doc-comment
 * cliente e o tipo do escritório ao mesmo tempo, e recarregar é o que impede a // ux-neutrality:allow doc-comment
 * tela de afirmar um estado que o servidor recusou. // ux-neutrality:allow doc-comment
 */
export function AdminClientesPage() {
  const apiClient = useApiClient();
  const [clientes, setClientes] = useState<ClienteAdmin[] | null>(null);
  const [escritorios, setEscritorios] = useState<EscritorioAdmin[] | null>(null);
  const [erroClientes, setErroClientes] = useState<string | null>(null);
  const [erroEscritorios, setErroEscritorios] = useState<string | null>(null);
  const [erroAcao, setErroAcao] = useState<string | null>(null);
  const [sucesso, setSucesso] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [busca, setBusca] = useState('');
  const [estado, setEstado] = useState<EstadoAdmin>('ativos');
  // Só a carga mais recente pode escrever no estado: a resposta lenta de um
  // filtro anterior chegaria depois e pintaria uma lista que já não é a pedida
  // (lição F-E51.F4-02).
  const pedidoRef = useRef(0);

  const carregar = useCallback(async () => {
    const pedido = pedidoRef.current + 1;
    pedidoRef.current = pedido;
    try {
      const termo = busca.trim();
      const pagina = await listEscritorios(apiClient, {
        ...(termo === '' ? {} : { busca: termo }),
        estado,
        limite: 100,
      });
      if (pedido !== pedidoRef.current) return;
      setEscritorios(pagina.itens);
      setErroEscritorios(null);
    } catch (err) {
      if (pedido !== pedidoRef.current) return;
      setEscritorios([]);
      setErroEscritorios(_mensagem(err));
    }
    try {
      const lista = await listClientes(apiClient);
      if (pedido !== pedidoRef.current) return;
      setClientes(lista);
      setErroClientes(null);
    } catch (err) {
      if (pedido !== pedidoRef.current) return;
      setClientes([]);
      setErroClientes(_mensagem(err));
    }
  }, [apiClient, busca, estado]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  async function executar(acao: () => Promise<void>, mensagemSucesso: string): Promise<boolean> {
    setSalvando(true);
    setErroAcao(null);
    setSucesso(null);
    try {
      await acao();
      setSucesso(mensagemSucesso);
      await carregar();
      return true;
    } catch (err) {
      setErroAcao(_mensagem(err));
      return false;
    } finally {
      setSalvando(false);
    }
  }

  return (
    <section className="mx-auto flex w-full max-w-5xl flex-col gap-5 p-6">
      <header>
        <p className="mb-1.5 font-titulo text-rotulo uppercase text-fg-dim">
          {ptBR.admin.clientesEyebrow}
        </p>
        <h1 className="text-display-md text-fg">{ptBR.admin.clientesTitle}</h1>
        <p className="mt-1 text-body-md text-fg-muted">{ptBR.admin.clientesIntro}</p>
      </header>

      {sucesso !== null ? (
        <p role="status" data-testid="admin-sucesso" className="text-body-sm text-accent-700">
          {sucesso}
        </p>
      ) : null}
      {erroAcao !== null ? (
        <p role="alert" data-testid="admin-erro-acao" className="text-body-sm text-danger-700">
          {erroAcao}
        </p>
      ) : null}

      <ClientesSection
        clientes={clientes}
        erro={erroClientes}
        salvando={salvando}
        onCriar={(nome, cnpj) =>
          executar(async () => {
            await criarCliente(apiClient, { nome, cnpj });
          }, ptBR.admin.novoClienteSucesso)
        }
      />

      <div className="flex flex-wrap items-end gap-3">
        <label className="flex min-w-[220px] flex-col gap-1 text-body-sm text-fg-muted">
          <span>{ptBR.admin.buscaLabel}</span>
          <Input
            value={busca}
            placeholder={ptBR.admin.buscaPlaceholder}
            onChange={(evento) => setBusca(evento.target.value)}
            data-testid="admin-busca"
          />
        </label>
        <label className="flex flex-col gap-1 text-body-sm text-fg-muted">
          <span>{ptBR.admin.estadoLabel}</span>
          <Select
            value={estado}
            onChange={(evento) => setEstado(evento.target.value as EstadoAdmin)}
            data-testid="admin-estado"
          >
            <option value="ativos">{ptBR.admin.estadoAtivos}</option>
            <option value="inativos">{ptBR.admin.estadoInativos}</option>
            <option value="todos">{ptBR.admin.estadoTodos}</option>
          </Select>
        </label>
      </div>

      <EscritoriosSection
        escritorios={escritorios}
        clientes={clientes ?? []}
        erro={erroEscritorios}
        salvando={salvando}
        onVincular={(escritorioId, bancoId) =>
          executar(async () => {
            await definirVinculo(apiClient, escritorioId, bancoId);
          }, ptBR.admin.acaoSucesso)
        }
        onMarcarPlataforma={(escritorioId, isPlatform) =>
          executar(async () => {
            await definirPlataforma(apiClient, escritorioId, isPlatform);
          }, ptBR.admin.acaoSucesso)
        }
        onEncerrar={(escritorioId, encerrado) =>
          executar(
            async () => {
              await definirEncerramento(apiClient, escritorioId, encerrado);
            },
            encerrado ? ptBR.admin.encerrarSucesso : ptBR.admin.reabrirSucesso,
          )
        }
      />

      <NovoEscritorioForm
        clientes={clientes ?? []}
        salvando={salvando}
        onCriar={(payload) =>
          executar(async () => {
            await criarEscritorio(apiClient, payload);
          }, ptBR.admin.novoEscritorioSucesso)
        }
      />
    </section>
  );
}

export default AdminClientesPage;
